import React, { useState } from "react";
import Timer from "@/components/common/Timer";
import LiveTimer from "@/components/common/LiveTimer";
import CategoryHeader from "@/components/common/CategoryHeader";
import ButtonGroup from "@/components/common/buttons/ButtonGroup";
import { getChoiceColor } from "@/lib/utils";
import QuestionChoice from "@/components/common/QuestionChoice";
import LikeIcon from "@/components/icons/LikeIcon";
import Feedback from "@/components/common/Feedback";
import { motion } from "framer-motion"
import LiveBanner from '@/components/live/LiveBanner';
import { GAME_MODES } from "@/lib/constants.mjs";


const DailyQuestion = ({
  question,
	gameMode = GAME_MODES.endless,
  gameModeTitle,
  category,
  categoryColor,
  categoryMask,
  choices = [],
  timeTotal,
  timeRemaining,
	mainTimeRemaining,
  answer,
  userChoice,
  isTimerRunning,
  monotone = false,
  onUserChoice,
  onNext,
	primaryButtonLabel,
  secondaryButtonLabel = "Back",
  onQuit,
  isLastQuestion = false,
  feedback,
  onFeedback,
	feedbackColor = '#1D2142',
  playerCount = 0,
	crowdAnswers = {},
	liveScore,
	liveStreak,
	avatar,
}) => {
  const bgColor = "bg-white text-primary";
  const questionRef = React.useRef(null);

  // automatically focus question text for better accessibility
  React.useEffect(() => {
    questionRef.current.focus();
  }, [question]);

	const getPercentage = (option) =>
		(option in crowdAnswers) 
			? (Math.floor((crowdAnswers[option] / Object.values(crowdAnswers).reduce((a, b) => a + b, 0)) * 100)) 
			: 0

	const getCount = (option) => (option in crowdAnswers) ? crowdAnswers[option] : 0

	return (
		<motion.div
    initial={{ opacity: 0, x: -200}}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }} className={`mt-auto mb-auto inline-flex flex-col flex-grow items-center w-[75%] max-w-[1040px] min-w-[360px] md:p-8 md:gap-6 gap-2`}>
			<LiveBanner 
				playerCount={playerCount}
				score={liveScore}
				streak={liveStreak}
				avatar={avatar}
			/>
			<CategoryHeader
				gameMode={gameModeTitle}
				category={category}
				pieMask={categoryMask}
				monotone={monotone}
				color={categoryColor}
			/>
			<div className={'flex flex-col w-full md:gap-0 lg:gap-6 m-1 xs:m-0'}>
				<div className={'flex lg:flex-row flex-col md:gap-4 gap-2 w-full'}>
					<p tabIndex={0} ref={questionRef} data-testid="question-text" className={'flex flex-col w-full justify-center text-primary font-gBold bg-white bg-opacity-75 rounded-lg backdrop-blur md:p-8 p-3 md:text-[42px] text-[24px] md:leading-[42px] leading-[28px] text-center'}>
						{question?.replaceAll(/\{/g,'').replaceAll(/}/g, '')}
					</p>
					<div className={'lg:invisible visible lg:w-0 lg:overflow-hidden m-1 xs:m-0'} >
					{
						gameMode === GAME_MODES.live ? (
							<LiveTimer total={timeTotal} stopPoint={timeRemaining} remaining={mainTimeRemaining}/>
						) : (
							<Timer total={timeTotal} remaining={timeRemaining} />
						)
					}					
					</div>
					<div data-testid="answer-choices" className={'flex flex-col w-full md:gap-4 gap-2'}>
						{choices?.map((option, i) =>
							<QuestionChoice
								key={i}
								color={categoryColor || "primary"}
								background={getChoiceColor(bgColor, option, userChoice, answer, (!timeTotal || isTimerRunning))}
								value={option?.replaceAll(/\{/g,'').replaceAll(/}/g, '')}
								onSelect={() => onUserChoice(option)}
								enabled={!userChoice && (!timeTotal || isTimerRunning)}
								selected={userChoice === option}
								isCorrect={(answer && answer === option) || ((userChoice === '' || !userChoice) && answer === option)}
								isWrong={answer && userChoice === option && answer !== option}
								answerCount={getCount(option)}
								percentFilled={getPercentage(option)}
							/>
						)}
					</div>
				</div>
				<div className={'lg:visible invisible lg:h-auto h-0'} >
					{
						gameMode === GAME_MODES.live ? (
							<LiveTimer total={timeTotal} stopPoint={timeRemaining} remaining={mainTimeRemaining}/>
						) : (
							<Timer total={timeTotal} remaining={timeRemaining} />
						)
					}
				</div>
			</div>
			{(onFeedback && !isTimerRunning) && <div className={'flex px-5 py-1 justify-center bg-white bg-opacity-20 rounded-md font-gSemi w-11/12'}><Feedback color={feedbackColor} feedback={feedback ? feedback[question._id] : null} onFeedback={onFeedback} /></div>}
			<ButtonGroup
				primaryLabel={primaryButtonLabel ? primaryButtonLabel : isLastQuestion ? "View Summary" : "Next Question"} 
				primaryAction={onNext} 
				primaryEnabled={gameMode === GAME_MODES.live || !!answer} 
				primaryDataTestId={isLastQuestion ? 'view-summary' : 'next-question'}
        secondaryLabel={secondaryButtonLabel}
				secondaryAction={onQuit} 
				secondaryDataTestId={'quit'}
				className={`${(!onFeedback || isTimerRunning) ? 'mt-3 md:mt-2' : ''} m-0.5 xs:m-0 text-sm`}
			/>
		</motion.div>
	);
};

export default DailyQuestion;
